import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  computed,
  inject,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { FlexLayoutModule, MediaObserver } from '@ngbracket/ngx-layout';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

import { toObservable } from '@angular/core/rxjs-interop';
import { MetaStore, RelationsStore, UserStore } from '@qtek/libs/store';
import { UserService } from '@qtek/shared/services';
import { CustomValidators } from '@qtek/shared/utils';

import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { ErrorComponent } from '@qtek/libs/ui';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@qtek/shared/components';
import { CompanyOutput, PersonApi, Staff, UserRole } from '@qtek/shared/models';
import { fullscreenClass, minWidth } from '../../default-config';
import {
  InviteContactDialogComponent,
  inviteContactDialogConfig,
} from '../invite-contact-dialog/invite-contact-dialog.component';

export const employeeDialogConfig: MatDialogConfig = {
  panelClass: fullscreenClass,
  minWidth,
};

@Component({
  selector: 'qt-employee-dialog',
  templateUrl: './employee-dialog.component.html',
  styleUrls: ['./employee-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
    TranslationCoreModule,
    MatIconModule,
    ErrorComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatExpansionModule,
    MatSelectModule,
    MatButtonModule,
    MatDialogModule,
    FlexLayoutModule,
    AsyncPipe,
    MatCheckboxModule,
  ],
})
export class EmployeeDialogComponent implements OnInit, OnDestroy {
  private dialogData = inject<Staff>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<EmployeeDialogComponent>>(MatDialogRef);
  private fb = inject(UntypedFormBuilder);
  private userStore = inject(UserStore);
  private metaStore = inject(MetaStore);
  private relationsStore = inject(RelationsStore);
  private injector = inject(Injector);
  private matDialog = inject(MatDialog);
  media = inject(MediaObserver);

  protected readonly languages = this.metaStore.languages;
  protected readonly staffRoles = computed(() => this.metaStore.roles().staff);

  /**
   * Determines if form is in customer/vendor or linking stage
   *
   * @type {boolean}
   * @memberof EmployeeDialogComponent
   */
  link = false;

  /**
   * List of companies that are related to email
   *
   * @type {CompanyOutput[]}
   * @memberof EmployeeDialogComponent
   */
  companies: CompanyOutput[];

  /**
   * Holds email control, that is used to determine relationships and form mode
   *
   * @type {ExtendedFormControl}
   * @memberof EmployeeDialogComponent
   */
  email: UntypedFormControl;
  roles: UntypedFormControl;
  active: UntypedFormControl;
  edit: boolean;

  isAccountExists = false;
  userService = inject(UserService);
  inviteLanguage: UntypedFormControl;
  currentLanguageName: Observable<string>;
  destroy$ = new Subject<void>();

  constructor() {
    this.metaStore.loadRoles();
    this.metaStore.loadLanguages();
    this.edit = !!this.dialogData?._id;
  }

  ngOnInit(): void {
    this.createForm();
  }

  /**
   * Determines if form is valid
   *
   * @readonly
   * @type {boolean}
   * @memberof EmployeeDialogComponent
   */
  get isValid(): boolean {
    return this.isEmailValidAndLinked
      ? true
      : this.isAccountExists
        ? true
        : this.isAllAttributes;
  }

  get isEmailValidAndLinked() {
    return this.email.valid && this.link;
  }

  get isRolesInAddingMode() {
    return this.edit ? true : this.roles.valid;
  }

  get isAllAttributes() {
    return (
      this.isRolesInAddingMode && (this.email.valid || this.email.disabled)
    );
  }

  get personalData(): PersonApi {
    return {
      language: this.inviteLanguage
        ? this.inviteLanguage.value
        : this.dialogData.personApi.language,
    };
  }

  get payload(): Staff {
    return {
      email: this.email.value,
      role: this.roles.value,
      personApi: this.personalData,
    };
  }

  /**
   * Set up forms and controls
   *
   *
   * @memberof EmployeeDialogComponent
   */
  createForm(): void {
    this.email = this.fb.control('', [CustomValidators.email]);

    this.roles = this.fb.control('20');

    this.active = this.fb.control(true);
    // preventing from deactivating currently logged-in user
    //todo check user start
    toObservable(this.userStore.user, { injector: this.injector })
      .pipe(takeUntil(this.destroy$))
      .subscribe(userRelation => {
        if (!this.edit || userRelation?._id === this.dialogData._id)
          this.active.disable();
      });

    if (this.edit) {
      this.setDataForEditMode();
    } else {
      const lang = this.userStore.currentPersonLanguage();
      this.inviteLanguage = new UntypedFormControl(lang);
      this.currentLanguageName = combineLatest([
        this.inviteLanguage.valueChanges.pipe(startWith(lang)),
        toObservable(this.metaStore.languages, { injector: this.injector }),
      ]).pipe(map(([id, langs]) => langs.find((l: any) => l.id === id).name));
    }
  }

  setDataForEditMode(): void {
    if (this.dialogData.email) {
      this.email.setValue(this.dialogData.email);
      this.userService
        .checkUniqueEmailForLogged(this.dialogData.email)
        .subscribe(res => {
          this.isAccountExists = res.exist;
          this.email.reset({
            value: this.dialogData.email,
            disabled: true,
          });
        });
    }

    const prl = this.userStore.relationType();
    if (prl !== UserRole.OWNER) {
      this.roles.disable();
    }

    if (this.dialogData.role) {
      this.roles.setValue(this.dialogData.role.toString());
    }

    this.active.reset(this.dialogData.status < 900);
  }

  openIviteContactDialog(payload: Staff): void {
    this.matDialog.open(InviteContactDialogComponent, {
      ...inviteContactDialogConfig,
      data: {
        type: 'staff',
        person: payload.personApi,
        relId: payload._id,
      },
    });

    this.dialogRef.close(payload);
  }

  /**
   * Check email linked status
   *
   *
   * @memberof EmployeeDialogComponent
   */
  checkEmail(emailControl: UntypedFormControl): void {
    if (emailControl.valid) {
      this.userService
        .checkUniqueEmailForLogged(emailControl.value)
        .subscribe(res => {
          this.link = res.exist;
        });
    } else {
      this.link = false;
    }
  }

  onSubmit(): void {
    if (!this.isValid) {
      this.email.markAsDirty();
      this.roles.markAsDirty();
      this.active.markAsDirty();
      return;
    }

    if (this.edit) {
      this.editExistingEmployee();
    } else {
      this.saveNewEmployee();
    }
  }

  editExistingEmployee(): void {
    this.relationsStore
      .updateEmployee(
        {
          role: this.roles.value,
          status: this.active.value === false ? 900 : 1,
        },
        this.dialogData._id
      )
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: payload => this.dialogRef.close(payload),
        error: () => {
          /* dialog stays open on failure, mirroring the previous behavior */
        },
      });
  }

  saveNewEmployee(): void {
    if (this.link && this.roles.invalid) {
      this.roles.markAsDirty();
      return;
    }
    this.relationsStore
      .createEmployee(this.payload)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: payload => this.dialogRef.close(payload),
        error: () => {
          /* dialog stays open on failure, mirroring the previous behavior */
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
